import React, { useRef } from 'react';
import { VehicleSilhouetteId, SILHOUETTES } from '../../types/vehicle';
import { MODEL_CONFIGS } from '../../types/threeStudio';
import { Car, Truck, Flame, Upload, Check } from 'lucide-react';

export interface SilhouettePickerProps {
  selectedId: VehicleSilhouetteId;
  customSilhouetteDataUrl?: string;
  onSelectSilhouette: (id: VehicleSilhouetteId) => void;
  onUploadCustomImage: (dataUrl: string) => void;
}

export const SilhouettePicker: React.FC<SilhouettePickerProps> = ({
  selectedId,
  customSilhouetteDataUrl,
  onSelectSilhouette,
  onUploadCustomImage,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const silhouettesList: {
    id: VehicleSilhouetteId;
    title: string;
    year: string;
    wheelbase: string;
    icon: React.ReactNode;
    desc: string;
  }[] = [
    {
      id: 'classic_muscle',
      title: '1969 Fastback Muscle Coupe',
      year: '1969 Shelby Fastback 3D',
      wheelbase: '108.0" Wheelbase',
      icon: <Car className="w-5 h-5 text-safety-amber" />,
      desc: SILHOUETTES.classic_muscle.yearModelDescription,
    },
    {
      id: 'camaro_ss',
      title: '1969 Chevrolet Camaro SS',
      year: '1969 Camaro 396/Z28 3D',
      wheelbase: '108.0" Wheelbase',
      icon: <Car className="w-5 h-5 text-safety-cyan" />,
      desc: SILHOUETTES.camaro_ss.yearModelDescription,
    },
    {
      id: 'vintage_truck',
      title: 'Fleetside Shop Truck',
      year: '1967-72 C10 Short Bed',
      wheelbase: '115.0" Wheelbase',
      icon: <Truck className="w-5 h-5 text-safety-cyan" />,
      desc: SILHOUETTES.vintage_truck.yearModelDescription,
    },
    {
      id: 'mustang_fastback',
      title: '1969 Mustang Fastback',
      year: '1969 John Bowe SportsRoof 3D',
      wheelbase: '108.0" Wheelbase',
      icon: <Car className="w-5 h-5 text-safety-cyan" />,
      desc: SILHOUETTES.mustang_fastback.yearModelDescription,
    },
    {
      id: 'deuce_roadster',
      title: 'Deuce Highboy Roadster',
      year: '1932 3-Window / Roadster',
      wheelbase: '106.0" Wheelbase',
      icon: <Flame className="w-5 h-5 text-safety-amber" />,
      desc: SILHOUETTES.deuce_roadster.yearModelDescription,
    },
    {
      id: 'custom_upload',
      title: 'Custom Blueprint / Photo',
      year: 'Side Profile Ingestion',
      wheelbase: 'User-Calibrated Scale',
      icon: <Upload className="w-5 h-5 text-shop-muted" />,
      desc: customSilhouetteDataUrl
        ? 'Custom vehicle image loaded. Ready for stance tuning.'
        : 'Upload SVG or transparent PNG side profile for hub pinning.',
    },
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (result) {
        onUploadCustomImage(result);
        onSelectSilhouette('custom_upload');
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-shop-muted">
          Period Vehicle Silhouettes &amp; Custom Blueprint
        </h4>
        <span className="text-[11px] font-mono text-shop-muted">
          Scale: <strong className="text-shop-light font-bold">4.44 px/in</strong>
        </span>
      </div>

      {/* Grid of Silhouette Selection Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {silhouettesList.map((item) => {
          const isSelected = selectedId === item.id;
          return (
            <button
              key={item.id}
              onClick={() => {
                if (item.id === 'custom_upload' && !customSilhouetteDataUrl) {
                  fileInputRef.current?.click();
                } else {
                  onSelectSilhouette(item.id);
                }
              }}
              className={`min-h-[72px] p-3.5 rounded border text-left flex items-start gap-3 transition-all duration-100 cursor-pointer ${
                isSelected
                  ? 'bg-shop-panel text-shop-highlight border-safety-amber shadow-[inset_0_2px_0_0_#f59e0b]'
                  : 'bg-shop-dark text-shop-light border-shop-border hover:bg-shop-well hover:border-shop-steel'
              }`}
            >
              <div
                className={`p-2.5 rounded border shrink-0 ${
                  isSelected
                    ? 'bg-shop-darkest border-safety-amber text-safety-amber'
                    : 'bg-shop-panel border-shop-border text-shop-muted'
                }`}
              >
                {item.icon}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-bold uppercase tracking-wide truncate">
                    {item.title}
                  </span>
                  {isSelected && <Check className="w-4 h-4 text-safety-amber shrink-0" />}
                </div>

                {MODEL_CONFIGS[item.id]?.isPlaceholderMesh && (
                  <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-safety-amber/15 border border-safety-amber text-[9px] font-mono font-bold uppercase tracking-wider text-safety-amber">
                    Placeholder Mesh
                  </span>
                )}

                <div className="text-[11px] font-mono text-safety-amber font-semibold mt-0.5">
                  {item.year}
                </div>

                <p className="text-[11px] text-shop-muted line-clamp-1 mt-0.5">
                  {item.desc}
                </p>

                <div className="text-[10px] font-mono text-shop-muted mt-1">
                  {item.wheelbase}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Hidden File Input for Custom Upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/svg+xml,image/webp"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Custom Upload Action Row */}
      {selectedId === 'custom_upload' && (
        <div className="p-3 bg-shop-darkest border border-shop-border rounded flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs font-mono text-shop-muted">
            {customSilhouetteDataUrl ? (
              <span className="text-safety-green font-bold">Image loaded into canvas engine</span>
            ) : (
              <span>No image uploaded yet</span>
            )}
          </div>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="min-h-[44px] px-4 py-2 rounded bg-shop-panel hover:bg-shop-well text-shop-highlight border border-shop-border text-xs font-mono font-bold uppercase tracking-wider flex items-center gap-2 cursor-pointer transition-all duration-100"
          >
            <Upload className="w-4 h-4 text-safety-amber" />
            <span>{customSilhouetteDataUrl ? 'Replace Blueprint Image' : 'Select Side Profile File'}</span>
          </button>
        </div>
      )}
    </div>
  );
};
